/**
 * The route tree — code-based, no file-router plugin. Two shells: the public
 * site at `/`, the admin at `/beheer`. Admin is unauthenticated in Phase 0 and
 * says so on screen; Google OIDC arrives in Phase 1 (F-9).
 */

import { plainDate, VERHUURTYPES, type Verhuurtype } from "@bivak/domain";
import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { AdminLayout } from "./components/AdminLayout.js";
import { PublicLayout } from "./components/PublicLayout.js";
import { nl } from "./copy/nl.js";
import { AanvraagPage } from "./pages/Aanvraag.js";
import { BeheerAfrekeningPage } from "./pages/beheer/Afrekening.js";
import { BeheerAfrekeningenPage } from "./pages/beheer/Afrekeningen.js";
import { BeheerBoekingDetailPage } from "./pages/beheer/BoekingDetail.js";
import { BeheerBoekingenPage } from "./pages/beheer/Boekingen.js";
import { BeheerKalenderPage } from "./pages/beheer/Kalender.js";
import { BeheerTarievenPage } from "./pages/beheer/Tarieven.js";
import { KalenderPage } from "./pages/Kalender.js";
import { PraktischPage } from "./pages/Praktisch.js";

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => <p className="py-20 text-center text-mist">{nl.fouten.nietGevonden}</p>,
});

const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: PublicLayout,
});

const kalenderRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/",
  component: KalenderPage,
});

const praktischRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/praktisch",
  component: PraktischPage,
});

/** `?type=`, `?van=`, `?tot=` — how the type cards hand their choice to the form. */
type AanvraagSearch = {
  type?: Verhuurtype | undefined;
  van?: string | undefined;
  tot?: string | undefined;
};

/**
 * A real calendar date, or nothing. The regex alone is not enough: it passes
 * `2026-13-45`, which `Intl` then crashes the page on, and `2026-02-31`, which
 * JS quietly rolls over to March — a wrong date rendered as the chosen weekend
 * is worse than an error. `plainDate` rejects both.
 */
function calendarDateOrUndefined(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  try {
    return plainDate(value).toString();
  } catch {
    return undefined;
  }
}

const aanvraagRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/aanvraag",
  validateSearch: (search: Record<string, unknown>): AanvraagSearch => {
    const type = VERHUURTYPES.find((candidate) => candidate === search.type);
    const van = calendarDateOrUndefined(search.van);
    const tot = calendarDateOrUndefined(search.tot);
    // An INVERTED stay is no stay; an equal pair is a day rental and stays.
    if (van !== undefined && tot !== undefined && tot < van) {
      return { type, van: undefined, tot: undefined };
    }
    return { type, van, tot };
  },
  component: AanvraagPage,
});

const beheerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/beheer",
  component: AdminLayout,
});

const beheerBoekingenRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/",
  component: BeheerBoekingenPage,
});

const beheerBoekingDetailRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/boekingen/$boekingId",
  component: BeheerBoekingDetailPage,
});

const beheerAfrekeningRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/boekingen/$boekingId/afrekening",
  component: BeheerAfrekeningPage,
});

const beheerKalenderRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/kalender",
  component: BeheerKalenderPage,
});

const beheerAfrekeningenRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/afrekeningen",
  component: BeheerAfrekeningenPage,
});

const beheerTarievenRoute = createRoute({
  getParentRoute: () => beheerRoute,
  path: "/tarieven",
  component: BeheerTarievenPage,
});

const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([kalenderRoute, praktischRoute, aanvraagRoute]),
  beheerRoute.addChildren([
    beheerBoekingenRoute,
    beheerBoekingDetailRoute,
    beheerAfrekeningRoute,
    beheerKalenderRoute,
    beheerAfrekeningenRoute,
    beheerTarievenRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
